import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useRecurly } from '@recurly/react-recurly';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Helmet } from 'react-helmet-async';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSubmit } from 'react-router-dom';

import { PaymentInterface } from './checkout/payment-methods';
import { AddressForm } from './checkout/address-form';
import { browserLocale } from '@/services/locale';
import { safeHref } from '@/utils/url-utils';
import { Button, CheckboxInput, Field, FieldGroup, FormContext, TextInput } from './form';
import { Cart } from  './checkout/cart';
import {
  Customer,
  PurchaseCreator,
  FormError,
  ThreeDSecureRequiredError,
  ReCaptchaRequiredError
} from '@/services/purchase';
import { FLAGS } from '@/services/session';
import { RecurlyWithInternals } from '@/types/recurly';
import { Theme } from './theme';
import { ThreeDSecureModal } from './checkout/three-d-secure-modal';
import { useCheckoutSession } from '@/hooks/use-checkout-session';
import { useLocale } from '@/hooks/use-locale';
import { CART_EMPTINESS_STATE } from '@/contexts/cart-context';
import { CartInternalContextProvider } from '@/contexts/cart-internal-context';
import { FirstAndLastName } from './checkout/first-and-last-name';
import { TaxIdentifier } from './checkout/tax-identifier';
import { ReflectiveAddress } from './checkout/reflective-address';
import { useCartContext } from '@/hooks/use-cart-context';
import { usePaymentMethodContext } from '@/hooks/use-payment-method-context';
import { SubscriptionDetails } from './checkout/subscription-details';
import { FreedomPayLogoImage } from './images/freedom-pay-logo';

const SHOW_TAX_IDENTIFIER_FOR_COUNTRY_CODES = ['BR', 'AR'];
const preferredCountry = browserLocale().region;

export function Checkout () {
  const recurly = useRecurly() as RecurlyWithInternals;
  const [billingAddressSameAsShipping, setBillingAddressSameAsShipping] = useState(false);
  const [checkoutSession, _setCheckoutSession] = useCheckoutSession();
  const [customer, setCustomer] = useState<Customer>({});
  const [canSubmit, setCanSubmit] = useState(true);
  const [error, setError] = useState<FormError>();
  const [policiesAcceptance, setPoliciesAcceptance] = useState(false);
  const [recurringChargesAcceptance, setRecurringChargesAcceptance] = useState(false);
  const [subscriptionAgreementAccepted, setSubscriptionAgreementAccepted] = useState(false);
  const [agreementStale, setAgreementStale] = useState(false);
  const [recaptchaV2ClientKey, setReCaptchaV2ClientKey] = useState('');
  const [recaptchaV2Result, setRecaptchaV2Result] = useState<string | null>('');
  const [required, setRequired] = useState<string[]>([]);
  const [showBillingAddressFields, setShowBillingAddressFields] = useState(false);
  const [showTaxIdentifierField, setShowTaxIdentifierField] = useState(false);
  const [threeDSecureActionTokenId, setThreeDSecureActionTokenId] = useState<string>();
  const [threeDSecureActionResultTokenId, setThreeDSecureActionResultTokenId] = useState<string>();
  const errorAlertRef = useRef(document.createElement('div'));
  const formRef = useRef(document.createElement('form'));
  const submit = useSubmit();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useLocale();

  const {
    cartEmptiness,
    requiresBillingInfo,
    billingAddress,
    pricing,
    setBillingAddress,
    shippingAddress,
    setShippingAddress
  } = useCartContext();

  const {
    billingInfoTokenCreator,
    billingInfoToken,
    usedExpressCheckout
  } = usePaymentMethodContext();

  const gatewayTypes = useMemo(() => {
    return checkoutSession.paymentMethods.reduce((acc, paymentMethod) => {
      paymentMethod.gateways.forEach(gateway => {
        if (!acc.includes(gateway.type)) {
          acc.push(gateway.type);
        }
      });
      return acc;
    }, [] as string[]);
  }, [checkoutSession.paymentMethods]);

  const onlyFreedomPay = gatewayTypes.length === 1 && gatewayTypes.includes('freedom_pay');

  const {
    id: checkoutSessionTokenId,
    cart: { currency },
    flags,
    logoUrl,
    iconUrl,
    paymentMethods,
    site: {
      addressRequirement,
      name: siteName,
      flags: siteFlags
    },
  } = checkoutSession;

  const cancelUrl = safeHref(checkoutSession.cancelUrl);
  const accountManagementUrl = safeHref(checkoutSession.accountManagementUrl);
  const confirmUrl = safeHref(checkoutSession.confirmUrl);
  const privacyPolicyUrl = safeHref(checkoutSession.privacyPolicyUrl);
  const tosUrl = safeHref(checkoutSession.tosUrl);
  const supportUrl = safeHref(checkoutSession.supportUrl);

  const purchaseCreator = new PurchaseCreator(
    billingInfoToken,
    billingInfoTokenCreator,
    checkoutSessionTokenId,
    formRef,
    recurly
  );

  const acceptShippingAddress = flags.includes(FLAGS.ACCEPT_SHIPPING_ADDRESS);

  const hasRecurringItems = useMemo(() => {
    const { subscriptions = [] } = checkoutSession.cart;

    return subscriptions.some(sub => Number(sub.quantity) > 0);
  }, [checkoutSession.cart]);

  const hasAutoRenewItems = useMemo(() => {
    if (!(pricing && 'items' in pricing && (pricing as any).items?.subscriptions)) return false;

    const hasAutoRenew = (pricing as any).items.subscriptions.some((subPricing: any) => {
      const plan = subPricing?.items?.plan;
      const quantity = plan?.quantity ?? 1;
      const autoRenew = plan?.auto_renew;

      return Number(quantity) > 0 && autoRenew === true;
    });

    return hasAutoRenew;
  }, [pricing]);

  // Flags / site capabilities
  const requiresRecurringConsent = flags.includes(FLAGS.REQUIRE_RECURRING_CHARGES_CONSENT);
  const canCancelWithHam = checkoutSession.site.flags?.includes(FLAGS.CAN_CANCEL_WITH_HAM);
  const taxEnabled = siteFlags?.includes(FLAGS.TAX_ENABLED);

  // Purchase classification
  const isSubscription = hasRecurringItems || hasAutoRenewItems; // any recurring or auto‑renew item
  const isOneTimePurchase = !isSubscription;

  // Policy presence & cart state
  const hasPolicies = !!(privacyPolicyUrl || tosUrl);
  const hasCartItems = cartEmptiness === CART_EMPTINESS_STATE.NOT_EMPTY;

  // Consent / agreement visibility & acceptance
  const shouldShowRecurringConsent = requiresRecurringConsent && isSubscription;
  const hasAcceptedAgreement = !requiresRecurringConsent || subscriptionAgreementAccepted;

  // Individual unmet requirements
  const needsRecurringConsent = shouldShowRecurringConsent && !recurringChargesAcceptance; // user must tick recurring consent
  const needsSubscriptionAgreement = requiresRecurringConsent && isSubscription && !subscriptionAgreementAccepted; // must accept agreement text

  // Prevent form fields from rendering until subscription agreement is accepted (prevents flickering)
  const shouldShowFormFields = !shouldShowRecurringConsent || subscriptionAgreementAccepted;

  // Combined consent acceptance logic
  // Rules summary:
  // - Recurring consent required only when shouldShowRecurringConsent.
  // - Policies acceptance required only when policies exist.
  // - If neither is presented, we consider consent implicitly granted.
  const recurringConsentSatisfied = !shouldShowRecurringConsent || recurringChargesAcceptance;
  const policiesSatisfied = !hasPolicies || policiesAcceptance;
  const allConsentsAccepted = recurringConsentSatisfied && policiesSatisfied;

  // Final submit disabled condition
  const isSubmitDisabled = ((hasCartItems || needsRecurringConsent || needsSubscriptionAgreement) && !allConsentsAccepted)
    || (shouldShowRecurringConsent && agreementStale);

  useEffect(() => {
    if (showBillingAddressFields) {
      if (addressRequirement === 'none') {
        setRequired(['country']);
      } else if (addressRequirement === 'zip') {
        setRequired(['country','postal_code']);
      } else if (addressRequirement === 'streetzip') {
        setRequired(['country','address1', 'postal_code']);
      } else if (addressRequirement === 'full') {
        setRequired(['address1', 'city', 'state', 'country', 'postal_code']);
      }
    } else {
      const nonAddressFields = required.filter(field =>
        !['address1', 'city', 'state', 'country', 'postal_code'].includes(field)
      );
      setRequired(nonAddressFields);
    }
  }, [addressRequirement, taxEnabled, showBillingAddressFields]);

  // Configures required fields
  useEffect(() => {
    recurly?.configure({ required, hostname: recurly.config.hostname });
  }, [required]);

  // Hides billing address when it's not required or when Express Checkout has been used
  useEffect(() => {
    if (!requiresBillingInfo) {
      setShowBillingAddressFields(false);
    } else {
      setShowBillingAddressFields(!usedExpressCheckout &&
        (taxEnabled || addressRequirement !== 'none'));
    }
  }, [currency, paymentMethods, requiresBillingInfo, usedExpressCheckout, taxEnabled, addressRequirement]);

  // Sets billing address from shipping address
  useEffect(() => {
    if (!billingAddressSameAsShipping) return;
    if (!shippingAddress) return;
    setBillingAddress(shippingAddress);
  }, [billingAddressSameAsShipping, shippingAddress]);

  // Sets a default shipping country
  useEffect(() => {
    if (!acceptShippingAddress) return;
    if (shippingAddress?.country) return;
    setShippingAddress({ ...shippingAddress, country: preferredCountry });
  }, [acceptShippingAddress]);

  // Translates billing address errors to shipping address when billing address is copied from shipping.
  // We do this because billing address is client- and server-validated and shipping address is only server-validated
  useEffect(() => {
    if (!error) return;
    if (!billingAddressSameAsShipping) return;
    if (!error.addressErrorsPresent()) return;
    setError(FormError.withDuplicatedAddressFields(error, name => `shipping[address][${name}]`));
  }, [billingAddressSameAsShipping, error]);

  useEffect(() => {
    setShowTaxIdentifierField(
      flags.includes(FLAGS.ACCEPT_TAX_IDENTIFIER)
      && SHOW_TAX_IDENTIFIER_FOR_COUNTRY_CODES.includes(billingAddress?.country || '')
    );
  }, [flags, billingAddress]);

  // Re-gates the agreement when quantities change after the user already accepted
  useEffect(() => {
    if (subscriptionAgreementAccepted) setAgreementStale(true);
  }, [checkoutSession.cart.subscriptions]);

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(undefined);

    setCanSubmit(false);

    let recaptchaV3Token;
    if (executeRecaptcha) {
      recaptchaV3Token = await executeRecaptcha('checkout');
    }

    try {
      const purchase = await purchaseCreator.create(
        currency,
        customer,
        shippingAddress,
        threeDSecureActionResultTokenId,
        recaptchaV2Result,
        recaptchaV3Token,
        policiesAcceptance,
        requiresBillingInfo,
        recurringChargesAcceptance
      );

      if (confirmUrl) {
        window.location.replace(confirmUrl);
      } else {
        submit({
          checkoutSession: {
            ...checkoutSession,
            purchases: [purchase]
          }
        }, {
          action: `/order/${checkoutSessionTokenId}`,
          method: 'post',
          encType: 'application/json'
        });
      }
    } catch (err) {
      if (err instanceof ThreeDSecureRequiredError) {
        setThreeDSecureActionResultTokenId(undefined);
        setThreeDSecureActionTokenId(err.threeDSecureActionTokenId);
      } else if (err instanceof ReCaptchaRequiredError) {
        setError(err);
        setReCaptchaV2ClientKey(err.reCaptchaClientKey);
        setCanSubmit(true);
      } else if (err instanceof FormError) {
        setError(err);
        setCanSubmit(true);
      } else {
        setCanSubmit(true);
        throw err;
      }
    }
  };

  useEffect(() => {
    if (!threeDSecureActionResultTokenId) return;
    handleSubmit();
  }, [threeDSecureActionResultTokenId]);

  useEffect(() => {
    const ref = errorAlertRef?.current;
    if (!ref) return;
    if ('scrollIntoView' in ref) ref.scrollIntoView({ behavior: 'smooth' });
  }, [error, errorAlertRef]);

  return (
    <FormContext.Provider value={{ error, handleSubmit }}>
      {iconUrl && <Helmet><link rel="icon" href={iconUrl} /></Helmet>}
      <Theme />
      <form onSubmit={handleSubmit} ref={formRef}>
        <div className="font-sans grid gap-x-8 min-w-80 px-4 lg:px-8 grid-cols-1 lg:grid-cols-[auto_auto_min-content]">
          <header className="w-full max-w-lg mx-auto mt-6 mb-10 order-1 lg:col-span-2 lg:mt-8">
            {logoUrl
              ? <img className="max-h-16 max-w-auto" src={logoUrl} alt={siteName} />
              : (
                <div className="text-3xl font-semibold">
                  {siteName}
                </div>
              )
            }

            {cancelUrl && (
              <div className="lg:max-w-lg mx-auto lg:mx-0 lg:pr-8 mt-4">
                <a href={cancelUrl}>← {t('back')}</a>
              </div>
            )}
          </header>

          <section className="order-2 lg:order-4">
            <CartInternalContextProvider>
              <Cart />
            </CartInternalContextProvider>
          </section>

          {cartEmptiness === CART_EMPTINESS_STATE.NO_ITEMS && (
            <div
              className="
                xs:mx-auto max-w-lg order-3 mb-8 p-5 rounded-lg bg-checkout-accent
                h-auto text-center py-8 text-xl font-normal text-checkout-subtle
                lg:col-span-2
              "
            >
              {t('cart.empty')}
            </div>
          )}

          {cartEmptiness !== CART_EMPTINESS_STATE.NO_ITEMS && (
            <section className="xs:mx-auto w-full max-w-lg order-3 lg:col-span-2">
              {error?.base && (
                <div
                  className="
                    col-span-2
                    p-2
                    mb-4
                    mx-auto
                    lg:max-w-lg
                    border-2
                    rounded
                    bg-red-50
                    border-red-400
                    text-red-400
                    dark:border-0
                    dark:bg-red-300
                    dark:text-red-700
                  "
                  ref={errorAlertRef}
                  style={{ scrollMargin: '2rem' }}
                >
                  {error.base.fullMessages}
                </div>
              )}

              {/* Subscription Details */}
              {shouldShowRecurringConsent && (
                <div className="mb-6">
                  <FieldGroup title={t('subscription.details.title')}>
                    {/* ref: use of `SubscriptionDetails` component */}
                    <SubscriptionDetails
                      agreed={subscriptionAgreementAccepted && !agreementStale}
                      onAgreementChange={() => {
                        setSubscriptionAgreementAccepted(true);
                        setAgreementStale(false);
                      }}
                    />
                  </FieldGroup>
                </div>
              )}

              {/* Cancel/Change Subscription Info */}
              {!shouldShowRecurringConsent && (canCancelWithHam ? cancelUrl : supportUrl) && (
                <div className="mb-4 text-sm text-gray-700">
                  To change or cancel your subscription,{' '}
                  {canCancelWithHam ? (
                    <a href={cancelUrl} target="_blank" rel="noopener noreferrer">click here</a>
                  ) : (
                    <a href={supportUrl} target="_blank" rel="noopener noreferrer">contact support</a>
                  )}.
                </div>
              )}

              {/* Payment and Address Fields - Only show after agreement or if no agreement required */}
              {shouldShowFormFields && (hasAcceptedAgreement || isOneTimePurchase) && (
                <div className="grid grid-cols-2 gap-4">
                  {/* Contact Info - Always show email, show other fields after agreement */}
                  <FieldGroup title={t('contact-info')}>
                    <Field
                      label={t('email-address')}
                      className="col-span-2"
                      name="email_address"
                      required
                    >
                      <TextInput
                        autoComplete="email"
                        onChange={event => setCustomer({ ...customer, emailAddress: event.target.value }) }
                      />
                    </Field>

                    {/* Show first/last name only after agreement or if no agreement required */}
                    { addressRequirement === 'none' && (
                      <FirstAndLastName
                        autoCompletePrefix="billing"
                        onCustomerChange={values => setCustomer({ ...customer, ...values })}
                        onAddressChange={values => setBillingAddress({ ...billingAddress, ...values })}
                      />
                    )}
                  </FieldGroup>

                  <PaymentInterface
                    setError={setError}
                  >
                    {acceptShippingAddress && (
                      <FieldGroup title={t('shipping-address')}>
                        <AddressForm
                          address={shippingAddress || {}}
                          addressRequirement='full'
                          customer={shippingAddress || {}}
                          namePrefix='shipping[address]'
                          required={required}
                          setAddress={setShippingAddress}
                          setCustomer={setShippingAddress}
                          showTaxIdentifierField={false}
                          taxEnabled={taxEnabled}
                        />
                      </FieldGroup>
                    )}
                  </PaymentInterface>

                  {showBillingAddressFields && (
                    <FieldGroup title={t('billing-address')}>
                      {acceptShippingAddress && (
                        <Field
                          name="billing-same-as-shipping"
                          className="col-span-2"
                        >
                          <CheckboxInput
                            checked={billingAddressSameAsShipping}
                            onChange={event => setBillingAddressSameAsShipping((event.target as HTMLInputElement).checked)}
                          >
                            {t('billing-address-same-as-shipping')}
                          </CheckboxInput>
                        </Field>
                      )}

                      {billingAddressSameAsShipping
                        ? (
                          <>
                            {shippingAddress && <ReflectiveAddress address={shippingAddress} />}
                            {showTaxIdentifierField && (
                              <TaxIdentifier
                                address={billingAddress || {}}
                                onAddressChange={values => setBillingAddress({ ...billingAddress, ...values })}
                              />
                            )}
                          </>
                        )
                        : (
                          <AddressForm
                            address={billingAddress || {}}
                            addressRequirement={addressRequirement}
                            customer={customer || {}}
                            required={required}
                            setAddress={setBillingAddress}
                            setCustomer={setCustomer}
                            showTaxIdentifierField={showTaxIdentifierField}
                            taxEnabled={taxEnabled}
                          />
                        )}
                    </FieldGroup>
                  )}

                  {shouldShowRecurringConsent && (
                    <Field
                      name="recurring_consent"
                      className="col-span-2 mt-4"
                    >
                      <CheckboxInput
                        checked={recurringChargesAcceptance}
                        onChange={event => setRecurringChargesAcceptance(event.target.checked)}
                      >
                        {t('recurring.consent')}{canCancelWithHam && (
                          accountManagementUrl ? (
                            t('recurring.cancel-with-link', { accountManagementUrl })
                          ) : (
                            ` ${t('recurring.cancel')}`
                          )
                        )}
                      </CheckboxInput>
                    </Field>
                  )}

                  {(privacyPolicyUrl || tosUrl) && (
                    <Field
                      name="policies_acceptance"
                      className="col-span-2 mt-4"
                    >
                      <CheckboxInput
                        checked={policiesAcceptance}
                        onChange={event => setPoliciesAcceptance(event.target.checked)}
                      >
                        {t('legal.accept')}
                        &nbsp;
                        {t.toSentence([
                          (privacyPolicyUrl && (
                            <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                              {t('legal.privacy-policy')}
                            </a>
                          )),
                          (tosUrl && (
                            <a href={tosUrl} target="_blank" rel="noopener noreferrer">
                              {t('legal.terms-of-service')}
                            </a>
                          ))
                        ].filter(Boolean))}
                      </CheckboxInput>
                    </Field>
                  )}

                  {recaptchaV2ClientKey && (
                    <div className="mt-6 col-start-1">
                      <ReCAPTCHA
                        sitekey={recaptchaV2ClientKey}
                        onChange={(result) => setRecaptchaV2Result(result)}
                      />
                    </div>
                  )}

                  <Button
                    type="submit"
                    className={'col-span-2 my-6'}
                    processing={!canSubmit}
                    disabled={isSubmitDisabled}
                  >
                    {requiresBillingInfo ? t('submit') : t('subscribe')}
                  </Button>

                  {onlyFreedomPay && (
                    <div className="col-span-2 mb-6 flex justify-center">
                      <FreedomPayLogoImage
                        className="w-auto relative z-50"
                        isDarkMode={document.documentElement.classList.contains('dark')}
                        data-testid="freedom-pay-logo"
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
        </div>
      </form>

      {threeDSecureActionTokenId && (
        <ThreeDSecureModal
          actionTokenId={threeDSecureActionTokenId}
          onCancel={() => {
            setThreeDSecureActionTokenId(undefined);
            setCanSubmit(true);
          }}
          onError={error => {
            setError(new FormError(error));
            setThreeDSecureActionTokenId(undefined);
            setCanSubmit(true);
          }}
          onToken={token => {
            setThreeDSecureActionResultTokenId(token.id);
            setThreeDSecureActionTokenId(undefined);
          }}
        />
      )}
    </FormContext.Provider>
  );
}
